import { invertSvg } from '@/utils/images.ts'
import { isPrint } from '@/utils/print'
import { cx } from '@/utils/strings'
import { AssetFile } from 'contentful'
import { useEffect, useRef, useState } from 'react'
import SafeHTML from './safe-html'

export type ImageContentfulProps = {
  contentful_id?: string
  title?: string
  description?: string
  publicUrl?: string
  file?: AssetFile
  svg?: {
    content: string
  }
  className?: any
  loading?: 'eager' | 'lazy'
  invertSvgForDarkMode?: boolean
}

export default function ImageContentful({
  contentful_id,
  title,
  description,
  publicUrl,
  file,
  svg,
  loading = 'lazy',
  invertSvgForDarkMode = false,
  className,
  ...rest
}: ImageContentfulProps) {
  if (!file && !publicUrl && !svg?.content) return
  const caption = description ? `${title} - ${description}` : title

  if (svg?.content) {
    return (
      <SafeHTML
        as="div"
        html={svg.content}
        {...rest}
        className={cx('w-full', className)}
      />
    )
  }

  if (['image/svg+xml'].includes(file?.contentType ?? '') || isPrint()) {
    let src = publicUrl || file?.url
    if (import.meta.env.APP_ENV === 'local') src = file?.url || publicUrl
    return <img data-testid="image" src={src} alt={caption} {...rest} className={className} />
  }

  // Generate Contentful image URLs with transformations
  const getTransformedUrl = (w: number, fm: string = 'webp', q: number = 80) => {
    // Use URLSearchParams to properly handle parameter encoding
    try {
      if (!file?.url) return '';
      const url = new URL(file.url)
      url.searchParams.set('w', w.toString())
      url.searchParams.set('fm', fm)
      url.searchParams.set('q', q.toString())
      return url.toString()
    } catch (e) {
      // Return original url if URL construction fails
      return file?.url || '';
    }
  }
  const height = file?.details?.image?.height || undefined
  const width = file?.details?.image?.width || undefined
  const maxWidth = Math.min(width || 1200, 1200)

  const [loaded, setLoaded] = useState(false)
  const [showLqip, setShowLqip] = useState(false)
  const imgRef = useRef<HTMLImageElement | null>(null)

  useEffect(() => {
    // Detect cached load
    const img = imgRef.current
    if (img?.complete && img.naturalWidth > 0) {
      setLoaded(true)
    }

    // Detect if image is still loading
    const timeoutId = setTimeout(() => {
      if (!loaded) setShowLqip(true)
    }, 1000)

    return () => clearTimeout(timeoutId)
  }, [])

  return (
    <picture className={cx('relative overflow-hidden block', className)}>
      {!loaded && (
        <div
          className={cx('absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-1000 ease-in', showLqip && 'opacity-100')}
        >
          <svg
            className="w-1/3 h-1/3 min-w-[32px] min-h-[32px] text-gray-300 dark:text-gray-600 animate-pulse"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            viewBox="0 0 20 18"
          >
            <path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z" />
          </svg>
        </div>
      )}

      {/* AVIF for best quality */}
      <source media="(max-width:400px)" srcSet={getTransformedUrl(400, 'avif')} type="image/avif" />
      <source media="(max-width:800px)" srcSet={getTransformedUrl(800, 'avif')} type="image/avif" />
      <source media="(min-width:801px)" srcSet={getTransformedUrl(maxWidth, 'avif')} type="image/avif" />
      {/* Fallback JPEG/PNG */}
      <img
        ref={imgRef}
        src={getTransformedUrl(maxWidth, 'jpg')}
        alt={caption}
        width={width}
        height={height}
        loading={loading}
        className={cx('transition-opacity duration-100 ease-in', loaded ? 'opacity-100' : 'opacity-0', className)}
        onLoad={() => setLoaded(true)}
      />
    </picture>
  )
}
